import { atom } from 'jotai';
import { atomWithDefault, atomWithStorage } from 'jotai/utils';
import { withAtomEffect } from 'jotai-effect';
import { atomFamily } from 'jotai-family';
import { loadable } from 'jotai-loadable';
import * as Sentry from '@sentry/react';
import { startOfDay } from 'date-fns';
import { dequal } from 'dequal/lite';

import { atomFamilyWithDefault, createSimpleState, createState } from '@rewardopl/jotai';
import { get as networkGet, post } from '@rewardopl/utils/network';
import { stringify } from '@rewardopl/utils/url';

import { PROD } from './env';

import type {
  Account,
  AccountRole,
  BaseDocument,
  Business,
  Card,
  CardSubscription,
  Challenge,
  Config,
  Coupon,
  CouponActivation,
  CouponWithStats,
  Device,
  Email,
  Feedback,
  Ingredient,
  Integration,
  IntegrationBusiness,
  IntegrationOrder,
  Level,
  Message,
  Offer,
  PlaceWithDynamicProperties,
  Plan,
  PlanSubscription,
  Poll,
  PollQuestion,
  PollQuestionAnswer,
  ProductWithDynamicProperties,
  PromotionGroup,
  Quiz,
  RecipientWithNotRegistered as Recipient,
  RecipientWithoutNotRegistered,
  Reward,
  SecurityEvent,
  SMS,
  StripeInvoiceSummary,
  StripePriceSummary,
  Transaction,
  User,
  Workflow,
} from '@rewardopl/types';
import type { Getter, SetStateAction } from 'jotai';
import type { ChallengeRanking, Session, StatArgs, Stats, StatsSummary } from './types';

type PollResponseSummary = {
  questions: (Pick<PollQuestion, '_id' | 'question' | 'type'> & {
    answers: ((PollQuestionAnswer | { text: string }) & { count: number })[];
  })[];
};

type AutomationSuggestion = Pick<Workflow, 'trigger' | 'actions' | 'conditions' | 'name'> & {
  description: string;
};

type SessionWithId = Session & {
  _id: string;
};

function mapItemsWithId<T>(
  items: T[],
  getId: (item: T, index: number) => string,
): (T & {
  _id: string;
})[] {
  return items.map((item, index) => ({
    ...item,
    _id: getId(item, index),
  }));
}

function mapSessionsWithId(sessions: Session[]): SessionWithId[] {
  return mapItemsWithId(sessions, (session, index) => `${session.expires_at}_${index}`);
}

function syncAccount(account: Account | null) {
  if (PROD) {
    Sentry.setUser(account ? { id: account._id, email: account.email } : null);
  }

  localStorage.setItem('account', JSON.stringify(account));
}

async function getAccount(signal?: AbortSignal): Promise<Account | null> {
  let response = null;
  try {
    response = await (networkGet('/api/accounts/current', { signal }) as Promise<Account>);
  } catch {}

  syncAccount(response);

  return response;
}

const accountWasCached = Boolean(localStorage.getItem('account'));

function readCachedAccount(): Account | null | undefined {
  const rawLocalStorageValue = localStorage.getItem('account');

  if (rawLocalStorageValue) {
    try {
      return JSON.parse(rawLocalStorageValue) as Account | null;
    } catch {}
  }

  return undefined;
}

const defaultMaybeCurrentAccountBaseAtom = atomWithDefault<
  Account | null | Promise<Account | null>
>(() => {
  const cached = readCachedAccount();

  if (cached !== undefined) {
    return cached;
  }

  return getAccount();
});

const defaultMaybeCurrentAccountState = withAtomEffect(
  defaultMaybeCurrentAccountBaseAtom,
  (_get, set) => {
    // Only refetch if account was initially in localStorage, therefore potentially stale
    if (!accountWasCached) {
      return;
    }

    const cached = readCachedAccount();

    const controller = new AbortController();

    const { signal } = controller;

    getAccount(signal).then((nextValue) => {
      if (signal.aborted) {
        return;
      }

      if (!dequal(nextValue, cached)) {
        set(defaultMaybeCurrentAccountBaseAtom, nextValue);
      }
    });

    return () => {
      controller.abort();
    };
  },
);

export const maybeCurrentAccountState = atom<
  Account | null | Promise<Account | null>,
  [SetStateAction<Account | null>],
  void
>(
  (get) => get(defaultMaybeCurrentAccountState),
  async (get, set, nextValueOrSetter: SetStateAction<Account | null>) => {
    const nextValue =
      typeof nextValueOrSetter === 'function'
        ? nextValueOrSetter(await get(defaultMaybeCurrentAccountState))
        : nextValueOrSetter;

    localStorage.setItem('account', JSON.stringify(nextValue));

    set(defaultMaybeCurrentAccountState, nextValue);
  },
);

export const maybeCurrentAccountLoadable = loadable(maybeCurrentAccountState);

export const currentAccountState = atom(
  async (get) => {
    const maybeAccount = await get(maybeCurrentAccountState);

    if (!maybeAccount) {
      throw new Error('Account not found');
    }

    return maybeAccount;
  },
  (_get, set, nextValue: Account) => {
    set(maybeCurrentAccountState, nextValue);
  },
);

function syncAccountRole(accountRole: AccountRole | null) {
  localStorage.setItem('account_role', JSON.stringify(accountRole));
}

async function getAccountRole(signal?: AbortSignal): Promise<AccountRole | null> {
  let response = null;
  try {
    response = await (networkGet('/api/account_roles/current', { signal }) as Promise<AccountRole>);
  } catch {}

  syncAccountRole(response);

  return response;
}

const accountRoleWasCached = localStorage.getItem('account_role');

function readCachedAccountRole(): AccountRole | null | undefined {
  const rawLocalStorageValue = localStorage.getItem('account_role');

  if (rawLocalStorageValue) {
    try {
      return JSON.parse(rawLocalStorageValue) as AccountRole | null;
    } catch {}
  }

  return undefined;
}

const defaultMaybeCurrentAccountRoleBaseAtom = atomWithDefault<
  AccountRole | null | Promise<AccountRole | null>
>(() => {
  const cached = readCachedAccountRole();

  if (cached !== undefined) {
    return cached;
  }

  return getAccountRole();
});

const defaultMaybeCurrentAccountRoleState = withAtomEffect(
  defaultMaybeCurrentAccountRoleBaseAtom,
  (_get, set) => {
    // Only refetch if account role was initially in localStorage, therefore potentially stale
    if (!accountRoleWasCached) {
      return;
    }

    const cached = readCachedAccountRole();

    const controller = new AbortController();

    const { signal } = controller;

    getAccountRole(signal).then((nextValue) => {
      if (signal.aborted) {
        return;
      }

      if (!dequal(nextValue, cached)) {
        set(defaultMaybeCurrentAccountRoleBaseAtom, nextValue);
      }
    });

    return () => {
      controller.abort();
    };
  },
);

export const maybeCurrentAccountRoleState = atom<
  AccountRole | null | Promise<AccountRole | null>,
  [SetStateAction<AccountRole | null>],
  void
>(
  (get) => get(defaultMaybeCurrentAccountRoleState),
  async (get, set, nextValueOrSetter: SetStateAction<AccountRole | null>) => {
    const nextValue =
      typeof nextValueOrSetter === 'function'
        ? nextValueOrSetter(await get(defaultMaybeCurrentAccountRoleState))
        : nextValueOrSetter;

    localStorage.setItem('account_role', JSON.stringify(nextValue));

    set(defaultMaybeCurrentAccountRoleState, nextValue);
  },
);

export const currentAccountRoleState = atom(
  async (get) => {
    const maybeAccountRole = await get(maybeCurrentAccountRoleState);

    if (!maybeAccountRole) {
      throw new Error('Account role not found');
    }

    return maybeAccountRole;
  },
  (_get, set, nextValue: AccountRole) => {
    set(maybeCurrentAccountRoleState, nextValue);
  },
);

function hasExactlyOneItem<T>(items: T[]): items is [T] {
  return items.length === 1;
}

export const currentIntegrationIdState = atom(async (get) => {
  const integrations = await get(integrationsState);

  // If there's no integrations, return undefined
  if (!integrations.length) {
    return undefined;
  }

  // If there's more than one integration, return the preferredIntegrationId, provided that the
  // integration with that id exists or id is null. If not, return null/undefined.
  const preferredIntegrationId = get(preferredIntegrationIdState);

  if (!preferredIntegrationId) {
    return preferredIntegrationId;
  }

  const preferredIntegration = integrations.find(
    (integration) => integration._id === preferredIntegrationId,
  );

  if (!preferredIntegration) {
    return undefined;
  }

  return preferredIntegrationId;
});

function syncBusiness(business: Business | null) {
  localStorage.setItem('business', JSON.stringify(business));
}

async function getBusiness(signal?: AbortSignal): Promise<Business | null> {
  let response = null;
  try {
    response = await (networkGet('/api/businesses/current', { signal }) as Promise<Business>);
  } catch {}

  syncBusiness(response);

  return response;
}

const businessWasCached = Boolean(localStorage.getItem('business'));

function readCachedBusiness(): Business | null | undefined {
  const rawLocalStorageValue = localStorage.getItem('business');

  if (rawLocalStorageValue) {
    try {
      return JSON.parse(rawLocalStorageValue) as Business | null;
    } catch {}
  }

  return undefined;
}

const defaultMaybeCurrentBusinessBaseAtom = atomWithDefault<
  Business | null | Promise<Business | null>
>(() => {
  const cached = readCachedBusiness();

  if (cached !== undefined) {
    return cached;
  }

  return getBusiness();
});

const defaultMaybeCurrentBusinessState = withAtomEffect(
  defaultMaybeCurrentBusinessBaseAtom,
  (_get, set) => {
    // Only refetch if business was initially in localStorage, therefore potentially stale
    if (!businessWasCached) {
      return;
    }

    const cached = readCachedBusiness();

    const controller = new AbortController();

    const { signal } = controller;

    getBusiness(signal).then((nextValue) => {
      if (signal.aborted) {
        return;
      }

      if (!dequal(nextValue, cached)) {
        set(defaultMaybeCurrentBusinessBaseAtom, nextValue);
      }
    });

    return () => {
      controller.abort();
    };
  },
);

export const maybeCurrentBusinessState = atom<
  Business | null | Promise<Business | null>,
  [SetStateAction<Business | null>],
  void
>(
  (get) => get(defaultMaybeCurrentBusinessState),
  async (get, set, nextValueOrSetter: SetStateAction<Business | null>) => {
    const nextValue =
      typeof nextValueOrSetter === 'function'
        ? nextValueOrSetter(await get(defaultMaybeCurrentBusinessState))
        : nextValueOrSetter;

    localStorage.setItem('business', JSON.stringify(nextValue));

    set(defaultMaybeCurrentBusinessState, nextValue);
  },
);

export const currentBusinessState = atom(
  async (get) => {
    const maybeBusiness = await get(maybeCurrentBusinessState);

    if (!maybeBusiness) {
      throw new Error('Business not found');
    }

    return maybeBusiness;
  },
  async (get, set, nextValueOrSetter: SetStateAction<Business>) => {
    set(
      maybeCurrentBusinessState,
      typeof nextValueOrSetter === 'function'
        ? nextValueOrSetter(await get(currentBusinessState))
        : nextValueOrSetter,
    );
  },
);

export const currentBusinessIdState = atom(async (get) => (await get(currentBusinessState))._id);

export const maybeCurrentBusinessIdState = atom(
  async (get) => (await get(maybeCurrentBusinessState))?._id ?? null,
);

export const maybeCurrentBusinessFlagsState = atom(
  async (get) => (await get(maybeCurrentBusinessState))?.flags ?? {},
);

export const currentPlanSubscriptionState = atom(async (get) => {
  const maybeBusinessId = await get(maybeCurrentBusinessIdState);

  if (!maybeBusinessId) {
    return null;
  }

  let response = null;
  try {
    response = await (networkGet(
      '/api/businesses/current/plan_subscriptions/current',
    ) as Promise<PlanSubscription>);
  } catch {}

  return response;
});

export const currentPlanSubscriptionInvoicesState = atom(async () => {
  const response = (await networkGet(
    '/api/businesses/current/plan_subscriptions/current/invoices',
  )) as StripeInvoiceSummary[];

  return mapItemsWithId(response, (invoice) => invoice.id);
});

export const currentPlanSubscriptionBillingPortalUrlState = atomFamily(
  (returnPath: string) =>
    atom(async () => {
      const response = (await networkGet(
        `/api/businesses/current/plan_subscriptions/current/billing_portal_url${stringify({
          return_path: returnPath,
        })}`,
      )) as { url: string };

      return response.url;
    }),
  dequal,
);

export const estimatedNumberOfCouponRecipientsQuery = atomFamily(
  (param: { cardId: Card['_id']; recipient: Recipient }) =>
    atom(async () => {
      const response = await (post(
        `/api/cards/${param.cardId}/coupons/estimate_number_of_recipients`,
        {
          body: { recipient: param.recipient },
        },
      ) as Promise<{ count: number }>);

      return response.count;
    }),
  dequal,
);

export const estimatedNumberOfEmailRecipientsQuery = atomFamily(
  (param: { businessId: Business['_id']; recipient: RecipientWithoutNotRegistered }) =>
    atom(async () => {
      const response = await (post(
        `/api/businesses/${param.businessId}/emails/estimate_number_of_recipients`,
        {
          body: { recipient: param.recipient },
        },
      ) as Promise<{ count: number }>);

      return response.count;
    }),
  dequal,
);

export const estimatedNumberOfMessageRecipientsQuery = atomFamily(
  (param: { businessId: Business['_id']; recipient: Recipient }) =>
    atom(async () => {
      const response = await (post(
        `/api/businesses/${param.businessId}/messages/estimate_number_of_recipients`,
        {
          body: { recipient: param.recipient },
        },
      ) as Promise<{ count: number }>);

      return response.count;
    }),
  dequal,
);

function isFullRecipient(
  recipient: Partial<RecipientWithoutNotRegistered> & {
    type: RecipientWithoutNotRegistered['type'];
  },
): recipient is RecipientWithoutNotRegistered {
  const { type } = recipient;

  switch (type) {
    case 'all':
    case 'registered':
      return true;
    case 'age_group':
      return 'age_group' in recipient && recipient.age_group !== undefined;
    case 'gender':
      return 'gender' in recipient && recipient.gender !== undefined;
    case 'level':
      return 'level_id' in recipient && recipient.level_id !== undefined;
    case 'likes_place':
      return 'place_id' in recipient && recipient.place_id !== undefined;
    case 'likes_product':
      return 'product_id' in recipient && recipient.product_id !== undefined;
    case 'promotion_group':
      return 'promotion_group_id' in recipient && recipient.promotion_group_id !== undefined;
    case 'rfm_group':
      return 'rfm_group' in recipient && recipient.rfm_group !== undefined;
    default:
      throw new Error(`Invalid recipient type: ${type satisfies never}`);
  }
}

export const estimatedNumberOfSmsRecipientsQuery = atomFamily(
  (param: { businessId: Business['_id']; recipient: RecipientWithoutNotRegistered }) =>
    atom(async () => {
      if (!isFullRecipient(param.recipient)) {
        return null;
      }

      const response = await (post(
        `/api/businesses/${param.businessId}/sms/estimate_number_of_recipients`,
        {
          body: { recipient: param.recipient },
        },
      ) as Promise<{ count: number }>);

      return response.count;
    }),
  dequal,
);

export const messagesCountQuery = atomFamilyWithDefault(
  (businessId: Business['_id']) => async () => {
    const response = await (networkGet(`/api/businesses/${businessId}/messages/count`) as Promise<{
      count: number;
    }>);

    return response.count;
  },
);

/**
 * Builds a query string for API requests.
 * - Falls back to stringify() for standard cases.
 * - When `sort_by` contains multiple comma-separated values, it expands them into repeated
 *   query params: `?sort_by=a&sort_by=b&sort=asc&sort=desc&...` while preserving other params.
 */
function stringifyWithArray(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  if ('sort_by' in params && typeof (params as { sort_by?: unknown }).sort_by === 'string') {
    const { sort_by } = params as { sort_by?: string } & Record<string, unknown>;

    if (sort_by?.includes(',')) {
      const {
        sort_by: sortBy = '',
        sort = '',
        ...paramsWithoutSort
      } = params as {
        sort?: string;
        sort_by?: string;
      } & Record<string, string | number | boolean | null | undefined>;

      const splitSortBy = sortBy.split(',');
      const splitSort = sort.split(',');

      const urlSearchParams = new URLSearchParams();

      for (const [key, value] of Object.entries(paramsWithoutSort)) {
        if (value !== undefined) {
          urlSearchParams.append(key, String(value));
        }
      }

      for (const sortByItem of splitSortBy) {
        urlSearchParams.append('sort_by', sortByItem);
      }

      for (const sortItem of splitSort) {
        urlSearchParams.append('sort', sortItem);
      }

      const qs = urlSearchParams.toString();

      return `?${qs}`;
    }
  }

  return stringify(params);
}

function createBusinessState<
  T extends BaseDocument,
  P1 extends string | Record<string, string | number | boolean | null | undefined> | undefined =
    | {
        search?: string;
        sort_by?: string;
        sort?: string;
      }
    | undefined,
  P2 extends P1 extends undefined
    ? string
    : Extract<P1, string> extends never
      ? string
      : [Extract<P1, string>, string] = P1 extends undefined
    ? string
    : Extract<P1, string> extends never
      ? string
      : [Extract<P1, string>, string],
>(getBaseAction: (get: Getter) => string | Promise<string>) {
  return createState(
    (param: P1) => async (get) => {
      const baseAction = await getBaseAction(get);

      const action =
        param && param instanceof Object ? `${baseAction}${stringifyWithArray(param)}` : baseAction;

      return networkGet(action) as Promise<T[]>;
    },
    (id: P2) => async (get) => {
      const baseAction = await getBaseAction(get);

      return networkGet(`${baseAction}/${id}`) as Promise<T>;
    },
  );
}

export const { itemsAtomFamily: accountsQuery, itemAtomFamily: accountQuery } =
  createBusinessState<Account>(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}accounts`;
  });

export const accountSessionsQuery = atomFamily(
  (accountId: Account['_id']) =>
    atom(async (get) => {
      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const baseAction = `/api/${maybeBusinessId ? 'businesses/current/' : ''}accounts/${accountId}/sessions`;

      const sessions = (await networkGet(baseAction)) as Session[];

      return mapSessionsWithId(sessions);
    }),
  dequal,
);

export const accountsState = atom(
  async (get) => get(accountsQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<Account[]>) => {
    set(accountsQuery(undefined), nextValueOrSetter);
  },
);

export const { itemsAtomFamily: accountFeedbackQuery, itemAtomFamily: accountFeedbackItemQuery } =
  createState(
    (
      param:
        | string
        | {
            accountId: Account['_id'];
            search?: string;
            sort_by?: string;
            sort?: string;
            with_comment?: string;
          },
    ) =>
      async (get) => {
        const paramObject = typeof param === 'string' ? { accountId: param } : param;
        const { accountId, ...paramRest } = paramObject;

        const maybeBusinessId = await get(maybeCurrentBusinessIdState);

        const baseAction = `/api/${maybeBusinessId ? 'businesses/current/' : ''}accounts/${accountId}/feedback`;

        return networkGet(`${baseAction}${stringify(paramRest)}`) as Promise<Feedback[]>;
      },
    ([baseId, id]) =>
      async (get) => {
        const maybeBusinessId = await get(maybeCurrentBusinessIdState);

        const baseAction = `/api/${maybeBusinessId ? 'businesses/current/' : ''}accounts/${baseId}/feedback/${id}`;

        return networkGet(baseAction) as Promise<Feedback>;
      },
  );

export const { itemsAtomFamily: accountRolesQuery, itemAtomFamily: accountRoleQuery } =
  createBusinessState<AccountRole>(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}account_roles`;
  });

export const accountRolesState = atom(
  async (get) => get(accountRolesQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<AccountRole[]>) => {
    set(accountRolesQuery(undefined), nextValueOrSetter);
  },
);

export const {
  itemsAtomFamily: accountTransactionsQuery,
  itemAtomFamily: accountTransactionQuery,
} = createState(
  (id) => async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    const baseAction = `/api/${maybeBusinessId ? 'businesses/current/' : ''}accounts/${id}/transactions`;

    return networkGet(baseAction) as Promise<Transaction[]>;
  },
  ([baseId, id]) =>
    async (get) => {
      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const baseAction = `/api/${maybeBusinessId ? 'businesses/current/' : ''}accounts/${baseId}/transactions/${id}`;

      return networkGet(baseAction) as Promise<Transaction>;
    },
);

export const { itemsAtomFamily: businessesQuery, itemAtomFamily: businessQuery } =
  createBusinessState<Business>(() => '/api/businesses');

export const businessesState = atom(
  async (get) => get(businessesQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<Business[]>) => {
    set(businessesQuery(undefined), nextValueOrSetter);
  },
);

export const { itemsAtomFamily: businessAccountsQuery, itemAtomFamily: businessAccountQuery } =
  createState(
    (id) => async () => networkGet(`/api/businesses/${id}/accounts`) as Promise<Account[]>,
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/businesses/${baseId}/accounts/${id}`) as Promise<Account>,
  );

export const {
  itemsAtomFamily: businessAccountRolesQuery,
  itemAtomFamily: businessAccountRoleQuery,
} = createState(
  (id) => async () => networkGet(`/api/businesses/${id}/account_roles`) as Promise<AccountRole[]>,
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/businesses/${baseId}/account_roles/${id}`) as Promise<AccountRole>,
);

export const { itemsAtomFamily: businessCardsQuery, itemAtomFamily: businessCardQuery } =
  createState(
    (id) => async () => networkGet(`/api/businesses/${id}/cards`) as Promise<Card[]>,
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/businesses/${baseId}/cards/${id}`) as Promise<Card>,
  );

export const { itemsAtomFamily: businessDevicesQuery, itemAtomFamily: businessDeviceQuery } =
  createState(
    (id) => async () => networkGet(`/api/businesses/${id}/devices`) as Promise<Device[]>,
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/businesses/${baseId}/devices/${id}`) as Promise<Device>,
  );

export const { itemsAtomFamily: businessEmailsQuery, itemAtomFamily: businessEmailQuery } =
  createState(
    (id: string | { businessId: Business['_id']; search?: string }) => async () => {
      const paramObject = typeof id === 'string' ? { businessId: id } : id;
      const { businessId, ...paramRest } = paramObject;

      const baseAction = `/api/businesses/${businessId}/emails`;

      return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<Email[]>;
    },
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/businesses/${baseId}/emails/${id}`) as Promise<Email>,
  );

export const {
  itemsAtomFamily: businessIntegrationsQuery,
  itemAtomFamily: businessIntegrationQuery,
} = createState(
  (id) => async () => networkGet(`/api/businesses/${id}/integrations`) as Promise<Integration[]>,
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/businesses/${baseId}/integrations/${id}`) as Promise<Integration>,
);

export const { itemsAtomFamily: businessMessagesQuery, itemAtomFamily: businessMessageQuery } =
  createState(
    (id: string | { businessId: Business['_id']; search?: string }) => async () => {
      const paramObject = typeof id === 'string' ? { businessId: id } : id;
      const { businessId, ...paramRest } = paramObject;

      const baseAction = `/api/businesses/${businessId}/messages`;

      return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<Message[]>;
    },
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/businesses/${baseId}/messages/${id}`) as Promise<Message>,
  );

export const businessMessagePollQuery = atomFamily(
  ([baseId, id]: [string, string]) =>
    atom(() => networkGet(`/api/businesses/${baseId}/messages/${id}/poll`) as Promise<Poll | null>),
  dequal,
);

export const businessMessagePollResponseSummaryQuery = atomFamily(
  ([baseId, id]: [string, string]) =>
    atom(
      () =>
        networkGet(
          `/api/businesses/${baseId}/messages/${id}/poll/response_summary`,
        ) as Promise<PollResponseSummary>,
    ),
  dequal,
);

export const { itemsAtomFamily: businessPlacesQuery, itemAtomFamily: businessPlaceQuery } =
  createState(
    (id) => async () =>
      networkGet(`/api/businesses/${id}/places`) as Promise<PlaceWithDynamicProperties[]>,
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/businesses/${baseId}/places/${id}`) as Promise<PlaceWithDynamicProperties>,
  );

export const { itemsAtomFamily: businessProductsQuery, itemAtomFamily: businessProductQuery } =
  createState(
    (id) => async () =>
      networkGet(`/api/businesses/${id}/products`) as Promise<ProductWithDynamicProperties[]>,
    ([baseId, id]) =>
      async () =>
        networkGet(
          `/api/businesses/${baseId}/products/${id}`,
        ) as Promise<ProductWithDynamicProperties>,
  );

export const { itemsAtomFamily: businessSmsQuery, itemAtomFamily: businessSmsItemQuery } =
  createState(
    (id: string | { businessId: Business['_id']; search?: string }) => async () => {
      const paramObject = typeof id === 'string' ? { businessId: id } : id;
      const { businessId, ...paramRest } = paramObject;

      const baseAction = `/api/businesses/${businessId}/sms`;

      return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<SMS[]>;
    },
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/businesses/${baseId}/sms/${id}`) as Promise<SMS>,
  );

export const {
  itemsAtomFamily: businessTransactionsQuery,
  itemAtomFamily: businessTransactionQuery,
} = createState(
  (id) => async () => networkGet(`/api/businesses/${id}/transactions`) as Promise<Transaction[]>,
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/businesses/${baseId}/transactions/${id}`) as Promise<Transaction>,
);

export const { itemsAtomFamily: cardsQuery, itemAtomFamily: cardQuery } = createBusinessState<Card>(
  async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}cards`;
  },
);

export const cardsState = atom(
  async (get) => get(cardsQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<Card[]>) => {
    set(cardsQuery(undefined), nextValueOrSetter);
  },
);

export const cardState = atom(async (get) => {
  const cards = await get(cardsState);

  if (hasExactlyOneItem(cards)) {
    return cards[0];
  }

  return null;
});

export const cardConfigQuery = atomFamilyWithDefault(
  (cardId: Card['_id']) => async () => networkGet(`/api/cards/${cardId}/config`) as Promise<Config>,
);

export const { itemsAtomFamily: cardChallengesQuery, itemAtomFamily: cardChallengeQuery } =
  createState(
    (
      param:
        | string
        | {
            cardId: Card['_id'];
            search?: string;
            sort_by?: string;
            sort?: string;
          },
    ) =>
      async () => {
        const paramObject = typeof param === 'string' ? { cardId: param } : param;
        const { cardId, ...paramRest } = paramObject;

        const baseAction = `/api/cards/${cardId}/challenges`;

        return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<Challenge[]>;
      },
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/cards/${baseId}/challenges/${id}`) as Promise<Challenge>,
  );

export const cardChallengeQuizQuery = atomFamily(
  ([baseId, id]: [string, string]) =>
    atom(() => networkGet(`/api/cards/${baseId}/challenges/${id}/quiz`) as Promise<Quiz | null>),
  dequal,
);

export const cardChallengeRankingQuery = atomFamily(
  ([baseId, id]: [string, string]) =>
    atom(
      () =>
        networkGet(`/api/cards/${baseId}/challenges/${id}/ranking`) as Promise<ChallengeRanking>,
    ),
  dequal,
);

export const { itemsAtomFamily: cardCouponsQuery, itemAtomFamily: cardCouponQuery } = createState(
  (
    param:
      | string
      | {
          cardId: Card['_id'];
          search?: string;
          sort_by?: string;
          sort?: string;
        },
  ) =>
    async () => {
      const paramObject = typeof param === 'string' ? { cardId: param } : param;
      const { cardId, ...paramRest } = paramObject;

      const baseAction = `/api/cards/${cardId}/coupons`;

      return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<
        CouponWithStats[]
      >;
    },
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/cards/${baseId}/coupons/${id}`) as Promise<CouponWithStats>,
);

export const { itemsAtomFamily: cardFeedbackQuery, itemAtomFamily: cardFeedbackItemQuery } =
  createState(
    (
      param:
        | string
        | {
            cardId: Card['_id'];
            search?: string;
            sort_by?: string;
            sort?: string;
            with_comment?: string;
          },
    ) =>
      async () => {
        const paramObject = typeof param === 'string' ? { cardId: param } : param;
        const { cardId, ...paramRest } = paramObject;

        const baseAction = `/api/cards/${cardId}/feedback`;

        return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<Feedback[]>;
      },
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/cards/${baseId}/feedback/${id}`) as Promise<Feedback>,
  );

export const {
  itemsAtomFamily: cardPromotionGroupsQuery,
  itemAtomFamily: cardPromotionGroupQuery,
} = createState(
  (id) => async () => networkGet(`/api/cards/${id}/promotion_groups`) as Promise<PromotionGroup[]>,
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/cards/${baseId}/promotion_groups/${id}`) as Promise<PromotionGroup>,
);

export const { itemsAtomFamily: cardRewardsQuery, itemAtomFamily: cardRewardQuery } = createState(
  (
    param:
      | string
      | {
          cardId: Card['_id'];
          search?: string;
          sort_by?: string;
          sort?: string;
        },
  ) =>
    async () => {
      const paramObject = typeof param === 'string' ? { cardId: param } : param;
      const { cardId, ...paramRest } = paramObject;

      const baseAction = `/api/cards/${cardId}/rewards`;

      return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<Reward[]>;
    },
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/cards/${baseId}/rewards/${id}`) as Promise<Reward>,
);

export const { itemsAtomFamily: cardWorkflowsQuery, itemAtomFamily: cardWorkflowQuery } =
  createState(
    (
      param:
        | string
        | {
            cardId: Card['_id'];
            search?: string;
            sort_by?: string;
            sort?: string;
          },
    ) =>
      async () => {
        const paramObject = typeof param === 'string' ? { cardId: param } : param;
        const { cardId, ...paramRest } = paramObject;

        const baseAction = `/api/cards/${cardId}/workflows`;

        return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<Workflow[]>;
      },
    ([baseId, id]) =>
      async () =>
        networkGet(`/api/cards/${baseId}/workflows/${id}`) as Promise<Workflow>,
  );

export const { itemsAtomFamily: cardSubscriptionsQuery, itemAtomFamily: cardSubscriptionQuery } =
  createBusinessState<CardSubscription>(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}card_subscriptions`;
  });

export const cardSubscriptionsState = atom(
  async (get) => get(cardSubscriptionsQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<CardSubscription[]>) => {
    set(cardSubscriptionsQuery(undefined), nextValueOrSetter);
  },
);

export const { itemsAtomFamily: cardSubscriptionCouponActivationsQuery } = createState(
  (id) => async () =>
    networkGet(`/api/card_subscriptions/${id}/coupon_activations`) as Promise<CouponActivation[]>,
  () => async () => {
    throw new Error('Coupon activation details are not available');
  },
);

export const {
  itemsAtomFamily: cardSubscriptionFeedbackQuery,
  itemAtomFamily: cardSubscriptionFeedbackItemQuery,
} = createState(
  (
    param:
      | string
      | {
          cardSubscriptionId: CardSubscription['_id'];
          search?: string;
          sort_by?: string;
          sort?: string;
          with_comment?: string;
        },
  ) =>
    async () => {
      const paramObject = typeof param === 'string' ? { cardSubscriptionId: param } : param;
      const { cardSubscriptionId, ...paramRest } = paramObject;

      const baseAction = `/api/card_subscriptions/${cardSubscriptionId}/feedback`;

      return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<Feedback[]>;
    },
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/card_subscriptions/${baseId}/feedback/${id}`) as Promise<Feedback>,
);

export const {
  itemsAtomFamily: cardSubscriptionTransactionsQuery,
  itemAtomFamily: cardSubscriptionTransactionQuery,
} = createState(
  (id) => async () =>
    networkGet(`/api/card_subscriptions/${id}/transactions`) as Promise<Transaction[]>,
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/card_subscriptions/${baseId}/transactions/${id}`) as Promise<Transaction>,
);

export const {
  itemsAtomFamily: cardCardSubscriptionsQuery,
  itemAtomFamily: cardCardSubscriptionQuery,
} = createState(
  (
    param:
      | string
      | {
          cardId: Card['_id'];
          offset?: number;
          limit?: number;
          search?: string;
          sort_by?: string;
          sort?: string;
        },
  ) =>
    async () => {
      const paramObject = typeof param === 'string' ? { cardId: param } : param;
      const { cardId, ...paramRest } = paramObject;

      const baseAction = `/api/cards/${cardId}/card_subscriptions`;

      return networkGet(`${baseAction}${stringifyWithArray(paramRest)}`) as Promise<
        CardSubscription[]
      >;
    },
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/cards/${baseId}/card_subscriptions/${id}`) as Promise<CardSubscription>,
);

export const currentPlanState = atom(async (get) => {
  const planSubscription = await get(currentPlanSubscriptionState);

  if (!planSubscription) {
    return null;
  }

  const currentPlan = await get(planQuery(planSubscription.plan_id));

  return currentPlan;
});

export const { itemsAtomFamily: devicesQuery, itemAtomFamily: deviceQuery } =
  createBusinessState<Device>(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}devices`;
  });

export const devicesState = atom(
  async (get) => get(devicesQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<Device[]>) => {
    set(devicesQuery(undefined), nextValueOrSetter);
  },
);

export const { itemsAtomFamily: deviceFeedbackQuery, itemAtomFamily: deviceFeedbackItemQuery } =
  createState(
    (
      param:
        | string
        | {
            deviceId: Device['_id'];
            search?: string;
            sort_by?: string;
            sort?: string;
            with_comment?: string;
          },
    ) =>
      async (get) => {
        const paramObject = typeof param === 'string' ? { deviceId: param } : param;
        const { deviceId, ...paramRest } = paramObject;

        const maybeBusinessId = await get(maybeCurrentBusinessIdState);

        const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/devices/${deviceId}/feedback`;

        return networkGet(`${baseAction}${stringify(paramRest)}`) as Promise<Feedback[]>;
      },
    ([baseId, id]) =>
      async (get) => {
        const maybeBusinessId = await get(maybeCurrentBusinessIdState);

        const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/devices/${baseId}/feedback/${id}`;

        return networkGet(baseAction) as Promise<Feedback>;
      },
  );

export const { itemsAtomFamily: deviceTransactionsQuery, itemAtomFamily: deviceTransactionQuery } =
  createState(
    (id) => async (get) => {
      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/devices/${id}/transactions`;

      return networkGet(baseAction) as Promise<Transaction[]>;
    },
    ([baseId, id]) =>
      async (get) => {
        const maybeBusinessId = await get(maybeCurrentBusinessIdState);

        const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/devices/${baseId}/transactions/${id}`;

        return networkGet(baseAction) as Promise<Transaction>;
      },
  );

export const { itemsAtomFamily: integrationsQuery, itemAtomFamily: integrationQuery } =
  createBusinessState<Integration>(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}integrations`;
  });

export const integrationsState = atom(
  async (get) => get(integrationsQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<Integration[]>) => {
    set(integrationsQuery(undefined), nextValueOrSetter);
  },
);

export const integrationBusinessQuery = atomFamily(
  (id: string) =>
    atom(async (get) => {
      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/integrations/${id}/business`;

      return networkGet(baseAction) as Promise<IntegrationBusiness>;
    }),
  dequal,
);

export const integrationItemNamesQuery = atomFamily(
  (id: string | null | undefined) =>
    atom(async (get) => {
      if (!id) {
        return [];
      }

      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/integrations/${id}/item_names`;

      return networkGet(baseAction) as Promise<string[]>;
    }),
  dequal,
);
export const integrationItemGroupNamesQuery = atomFamily(
  (id: string | null | undefined) =>
    atom(async (get) => {
      if (!id) {
        return [];
      }

      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/integrations/${id}/item_group_names`;

      return networkGet(baseAction) as Promise<string[]>;
    }),
  dequal,
);

export const integrationRecentOrdersQuery = atomFamilyWithDefault(
  (id: string | null | undefined) => async (get) => {
    if (!id) {
      return null;
    }

    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/integrations/${id}/recent_orders`;

    return networkGet(baseAction) as Promise<IntegrationOrder[]>;
  },
);

export const integrationOrderQuery = atomFamily(
  ([baseId, id]: [string, string]) =>
    atom(async (get) => {
      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/integrations/${baseId}/orders/${id}`;

      return networkGet(baseAction) as Promise<IntegrationOrder>;
    }),
  dequal,
);

export const {
  itemsAtomFamily: integrationProductsQuery,
  itemAtomFamily: integrationProductQuery,
} = createState(
  (id) => async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/integrations/${id}/products`;

    return networkGet(baseAction) as Promise<{ _id: string; name: string }[]>;
  },
  ([baseId, id]) =>
    async (get) => {
      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const baseAction = `/api${maybeBusinessId ? '/businesses/current' : ''}/integrations/${baseId}/products/${id}`;

      return networkGet(baseAction) as Promise<{ _id: string; name: string }>;
    },
);

export const { itemsAtomFamily: cardLevelsQuery, itemAtomFamily: cardLevelQuery } = createState(
  (id) => async () => networkGet(`/api/cards/${id}/levels`) as Promise<Level[]>,
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/cards/${baseId}/levels/${id}`) as Promise<Level>,
);

export const { itemsAtomFamily: cardOffersQuery, itemAtomFamily: cardOfferQuery } = createState(
  (id) => async () => networkGet(`/api/cards/${id}/offers`) as Promise<Offer[]>,
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/cards/${baseId}/offers/${id}`) as Promise<Offer>,
);

export const { itemsAtomFamily: ingredientsQuery, itemAtomFamily: ingredientQuery } =
  createBusinessState<Ingredient>(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}ingredients`;
  });

export const ingredientsState = atom(
  async (get) => get(ingredientsQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<Ingredient[]>) => {
    set(ingredientsQuery(undefined), nextValueOrSetter);
  },
);

export const { itemsAtomFamily: placesQuery, itemAtomFamily: placeQuery } =
  createBusinessState<PlaceWithDynamicProperties>(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}places`;
  });

export const placesState = atom(
  async (get) => get(placesQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<PlaceWithDynamicProperties[]>) => {
    set(placesQuery(undefined), nextValueOrSetter);
  },
);

export const { itemsAtom: plansState, itemAtomFamily: planQuery } = createSimpleState<
  Plan,
  Plan['_id']
>(
  async () => networkGet('/api/plans') as Promise<Plan[]>,
  (id) => async () => networkGet(`/api/plans/${id}`) as Promise<Plan>,
);

export const maybePlanQuery = atomFamily(
  (id: Plan['_id'] | undefined) => atom((get) => (id ? get(planQuery(id)) : null)),
  dequal,
);

export const stripePlanPricesState = atom(
  () => networkGet('/api/stripe/prices') as Promise<StripePriceSummary[]>,
);

export const preferredIntegrationIdState = atomWithStorage<Integration['_id'] | null | undefined>(
  'preferredIntegrationId',
  (() => {
    const rawLocalStorageValue = localStorage.getItem('preferredIntegrationId');

    return rawLocalStorageValue
      ? (JSON.parse(rawLocalStorageValue) as Integration['_id'] | null)
      : undefined;
  })(),
);

export const { itemsAtomFamily: productsQuery, itemAtomFamily: productQuery } =
  createBusinessState<ProductWithDynamicProperties>(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}products`;
  });

export const productsState = atom(
  async (get) => get(productsQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<ProductWithDynamicProperties[]>) => {
    set(productsQuery(undefined), nextValueOrSetter);
  },
);

export const placeAccountsQuery = atomFamilyWithDefault((id: string) => async (get) => {
  const accounts = await get(accountsQuery(undefined));

  return accounts.filter((account) => 'place_ids' in account && account.place_ids?.includes(id));
});

export const placeAccountQuery = atomFamilyWithDefault(
  ([_baseId, id]: [string, string]) =>
    async (get) =>
      get(accountQuery(id)),
);

export const placeDevicesQuery = atomFamilyWithDefault((id: string) => async (get) => {
  const devices = await get(devicesQuery(undefined));

  return devices.filter((product) => product.place_ids?.includes(id));
});

export const placeDeviceQuery = atomFamilyWithDefault(
  ([_baseId, id]: [string, string]) =>
    async (get) =>
      get(deviceQuery(id)),
);

export const placeProductsQuery = atomFamilyWithDefault((id: string) => async (get) => {
  const products = await get(productsQuery(undefined));

  return products.filter((product) => product.place_ids?.includes(id));
});

export const placeProductQuery = atomFamilyWithDefault(
  ([_baseId, id]: [string, string]) =>
    async (get) =>
      get(productQuery(id)),
);

export const statsSummaryQuery = atomFamily(
  (param: { from?: string; to?: string }) =>
    atom(async (get) => {
      const { from, to } = param;

      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const action = `/api/${maybeBusinessId ? `businesses/current/` : ''}stats/summary`;

      if (from || to) {
        const params: {
          from?: string;
          to?: string;
        } = {};
        if (from) {
          params.from = from;
        }
        if (to) {
          params.to = to;
        }

        return networkGet(`${action}${stringify(params)}`) as Promise<StatsSummary>;
      }

      return networkGet(action) as Promise<StatsSummary>;
    }),
  dequal,
);

const now = new Date();

const min = new Date(2020, 0, 1);
const max = startOfDay(now);

const promises = new Map<string, Promise<Stats | null>>();

const abortControllers = new Map<string, AbortController>();

export const statsQuery = atomFamily(
  (args: StatArgs) =>
    atom(async (get) => {
      const {
        /* users, transactions, rewards */
        type,
        /* new, total */
        style,
        from,
        to,
        place_id: placeId,
      } = args;

      const promisesArgs = JSON.stringify({ style, type, from, to, placeId });

      const existingPromise = promises.get(promisesArgs);

      if (existingPromise) {
        return existingPromise;
      }

      const abortControllersArgs = JSON.stringify({ style, type });

      // Abort the previous request
      const previousAbortController = abortControllers.get(abortControllersArgs);

      if (previousAbortController) {
        previousAbortController.abort('Aborted by new request');
      }
      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const action = `/api/${maybeBusinessId ? `businesses/current/` : ''}stats/${type}/${style}`;

      const controller = new AbortController();

      abortControllers.set(abortControllersArgs, controller);

      const { signal } = controller;

      if (from || to || placeId) {
        const params: Record<string, string> = {};

        if (from) {
          const fromWithTime = `${from}T00:00`;

          if (new Date(fromWithTime) < min) {
            return null;
          }

          params.from = from;
        }

        if (to) {
          const toWithtime = `${to}T00:00`;

          if (new Date(toWithtime) > max) {
            return null;
          }

          params.to = to;
        }

        if (placeId) {
          params.place_id = placeId;
        }

        const promise = networkGet(`${action}${stringify(params)}`, { signal }).then((response) => {
          abortControllers.delete(abortControllersArgs);

          return response;
        }) as Promise<Stats>;

        promises.set(promisesArgs, promise);

        return promise;
      }

      const promise = networkGet(action, { signal }).then((response) => {
        abortControllers.delete(abortControllersArgs);

        return response;
      }) as Promise<Stats>;

      promises.set(promisesArgs, promise);

      return promise;
    }),
  dequal,
);

export const statsLoadable = atomFamily((args: StatArgs) => loadable(statsQuery(args)), dequal);

export const suggestCouponsQuery = atomFamily(
  (params: { cardId: Card['_id'] }) =>
    atom(async () => {
      const baseAction = '/api/ai/suggest_coupons';

      return networkGet(`${baseAction}${stringify({ card_id: params.cardId })}`) as Promise<
        Pick<Coupon, 'name' | 'description' | 'availability' | 'benefits'>[]
      >;
    }),
  dequal,
);
export const suggestRewardsQuery = atomFamily(
  (params: { cardId: Card['_id'] }) =>
    atom(async () => {
      const baseAction = '/api/ai/suggest_rewards';

      return networkGet(`${baseAction}${stringify({ card_id: params.cardId })}`) as Promise<
        Pick<Reward, 'name' | 'description'>[]
      >;
    }),
  dequal,
);
export const suggestWorkflowsQuery = atomFamily(
  (params: { cardId: Card['_id'] }) =>
    atom(async () => {
      const baseAction = '/api/ai/suggest_workflows';

      return networkGet(`${baseAction}${stringify({ card_id: params.cardId })}`) as Promise<
        AutomationSuggestion[]
      >;
    }),
  dequal,
);

export const summarizeFeedbackQuery = atomFamily(
  (params: { cardId: Card['_id'] }) =>
    atom(async () => {
      const baseAction = '/api/ai/summarize_feedback';

      return networkGet(`${baseAction}${stringify({ card_id: params.cardId })}`) as Promise<string>;
    }),
  dequal,
);

export const { itemsAtomFamily: transactionsQuery, itemAtomFamily: transactionQuery } =
  createBusinessState<
    Transaction,
    | {
        created_from?: string;
        created_to?: string;
        place_id?: string;
        search?: string;
        sort?: string;
        sort_by?: string;
        type?: string;
      }
    | undefined
  >(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}transactions`;
  });

export const transactionsState = atom(
  async (get) => get(transactionsQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<Transaction[]>) => {
    set(transactionsQuery(undefined), nextValueOrSetter);
  },
);

export const { itemsAtomFamily: securityEventsQuery, itemAtomFamily: securityEventQuery } =
  createBusinessState<
    SecurityEvent,
    | {
        reason?: string;
        sort?: string;
        sort_by?: string;
        status?: string;
      }
    | undefined
  >(async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}security_events`;
  });

export const securityEventsState = atom(
  async (get) => get(securityEventsQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<SecurityEvent[]>) => {
    set(securityEventsQuery(undefined), nextValueOrSetter);
  },
);

function isPendingSecurityEvent(
  securityEvent: SecurityEvent,
): securityEvent is SecurityEvent & { status: 'pending' } {
  return securityEvent.status === 'pending';
}

export const pendingSecurityEventsCountQuery = atom(async (get) => {
  const securityEvents = await get(securityEventsState);

  return securityEvents.filter(isPendingSecurityEvent).length;
});

export const pendingSecurityEventsCountLoadable = loadable(pendingSecurityEventsCountQuery);

export const { itemsAtomFamily: usersQuery, itemAtomFamily: userQuery } = createBusinessState<User>(
  async (get) => {
    const maybeBusinessId = await get(maybeCurrentBusinessIdState);

    return `/api/${maybeBusinessId ? 'businesses/current/' : ''}users`;
  },
);

export const userSessionsQuery = atomFamily(
  (userId: User['_id']) =>
    atom(async (get) => {
      const maybeBusinessId = await get(maybeCurrentBusinessIdState);

      const baseAction = `/api/${maybeBusinessId ? 'businesses/current/' : ''}users/${userId}/sessions`;

      const sessions = (await networkGet(baseAction)) as Session[];

      return mapSessionsWithId(sessions);
    }),
  dequal,
);

export const usersState = atom(
  async (get) => get(usersQuery(undefined)),
  (_get, set, nextValueOrSetter: SetStateAction<User[]>) => {
    set(usersQuery(undefined), nextValueOrSetter);
  },
);

export const {
  itemsAtomFamily: userCardSubscriptionsQuery,
  itemAtomFamily: userCardSubscriptionQuery,
} = createState(
  (id) => async () =>
    networkGet(`/api/users/${id}/card_subscriptions`) as Promise<CardSubscription[]>,
  ([baseId, id]) =>
    async () =>
      networkGet(`/api/users/${baseId}/card_subscriptions/${id}`) as Promise<CardSubscription>,
);

export const pendingIntegrationRecentOrdersRefreshesState = atom<Integration['_id'][]>([]);
